import { useNavigate, useLocation } from 'react-router-dom';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import { useUser } from '../../lib/auth-client';
import { useAppSelector } from '../../store/hooks';

interface MenuContentProps {
	onNavigate?: () => void;
}

const mainListItems = [
	{ text: 'Bảng điều hành', icon: <DashboardRoundedIcon />, path: '/dashboard' },
	{ text: 'Khoa', icon: <BusinessRoundedIcon />, path: '/faculties' },
	{ text: 'Phương thức xét tuyển', icon: <AssignmentRoundedIcon />, path: '/admission-methods' },
	{ text: 'Ngành', icon: <CategoryRoundedIcon />, path: '/majors' },
	{
		text: 'Tỉnh/TP (Trước sáp nhập)',
		icon: <LocationOnRoundedIcon />,
		path: '/provinces-original',
	},
	{
		text: 'Tỉnh/TP (Sau sáp nhập)',
		icon: <LocationOnRoundedIcon />,
		path: '/provinces-merged',
	},
	{
		text: 'THPT',
		icon: <AccountBalanceRoundedIcon />,
		path: '/thpt',
	},
	{
		text: 'Các trường top 149',
		icon: <StarRoundedIcon />,
		path: '/top149',
	},
];

const secondaryListItems = [
	{ text: 'Cài đặt', icon: <SettingsRoundedIcon />, path: '/settings', adminOnly: false },
];

const adminListItems = [
	{ text: 'Quản lý hệ thống', icon: <AdminPanelSettingsIcon />, path: '/system', adminOnly: true },
	{ text: 'Quản lý tài khoản', icon: <ManageAccountsRoundedIcon />, path: '/system/account-management', adminOnly: true },
	{ text: 'Nhập dữ liệu', icon: <UploadFileRoundedIcon />, path: '/system/nhap-du-lieu', adminOnly: false },
	{ text: 'Xuất dữ liệu', icon: <DownloadRoundedIcon />, path: '/system/xuat-du-lieu', adminOnly: true },
	// Danh sách/tạo/upload chỉ cần `requireWritable`; preview/apply/revert/xoá cần
	// `requireAdmin` — enforce THẬT ở middleware server (Phase 3), không phải ở đây.
	{ text: 'Đợt chuyển đổi mã tỉnh', icon: <SwapHorizRoundedIcon />, path: '/system/chuyen-doi-tinh', adminOnly: false },
];

export default function MenuContent({ onNavigate }: MenuContentProps = {}) {
	const navigate = useNavigate();
	const location = useLocation();
	const { user } = useUser();
	const selectedFaculty = useAppSelector((state) => state.filters.selectedFaculty);

	const isAdmin = user?.role === 'admin';
	const canEdit = isAdmin || (user?.role === 'manager' && user?.writable === true);

	const handleNavigation = (path: string) => {
		navigate(path);
		onNavigate?.();
	};

	const isSelected = (path: string) => {
		return location.pathname === path;
	};

	return (
		<Stack sx={{ flexGrow: 1, p: 1, justifyContent: 'space-between' }}>
			<List dense>
				{mainListItems.map((item, index) => {
					if (item.path === '/faculties' && selectedFaculty && user?.role === "user") {
						return null;
					}
					return (
						<ListItem key={index} disablePadding sx={{ display: 'block' }}>
							<ListItemButton
								selected={isSelected(item.path || '')}
								onClick={() => handleNavigation(item.path || '')}
							>
								<ListItemIcon>{item.icon}</ListItemIcon>
								<ListItemText primary={item.text} />
							</ListItemButton>
						</ListItem>
					);
				})}
			</List>

			<List dense>
				{adminListItems.map((item, index) => {
					const canAccess = item.adminOnly ? isAdmin : canEdit;
					if (!canAccess) return null;
					return (
						<ListItem key={`admin-${index}`} disablePadding sx={{ display: 'block' }}>
							<ListItemButton
								selected={isSelected(item.path)}
								onClick={() => handleNavigation(item.path)}
							>
								<ListItemIcon>{item.icon}</ListItemIcon>
								<ListItemText primary={item.text} />
							</ListItemButton>
						</ListItem>
					);
				})}
				{secondaryListItems.map((item, index) => (
					<ListItem key={index} disablePadding sx={{ display: 'block' }}>
						<ListItemButton
							selected={isSelected(item.path)}
							onClick={() => handleNavigation(item.path)}
						>
							<ListItemIcon>{item.icon}</ListItemIcon>
							<ListItemText primary={item.text} />
						</ListItemButton>
					</ListItem>
				))}
			</List>
		</Stack>
	);
}
